import { DeliveryOrder } from "@/features/delivery/api/deliveryApi";
import { formatDuration, formatScheduledDateTime } from "@/features/driver/lib/driverPresentation.js";
import { useDriverLanguage } from "@/features/driver/i18n/useDriverLanguage";

interface JobSummaryProps {
  order: DeliveryOrder;
}

function formatTime(value?: string | null): string {
  return formatScheduledDateTime(value);
}

interface PhotoTile {
  id: string;
  label: string;
  url?: string | null;
}

/**
 * Read-only summary shown after a job is completed or marked no-show: the
 * timeline (scheduled → arrived → waiting → completed) and the captured photos.
 */
export default function JobSummary({ order }: JobSummaryProps) {
  const { t } = useDriverLanguage();
  const waitingSeconds = order.waiting_seconds ?? 0;

  const timeline: Array<{ id: string; label: string; value: string }> = [
    { id: "scheduled", label: t("scheduledLabel"), value: formatTime(order.scheduled_at) },
    { id: "arrived", label: t("arrivedBtn"), value: formatTime(order.arrived_at) },
    { id: "waiting", label: t("totalWaitingLabel"), value: waitingSeconds > 0 ? formatDuration(waitingSeconds) : "—" },
    { id: "completed", label: order.status === "no_show" ? t("markedNoShowLabel") : t("completedLabel"), value: formatTime(order.completed_at) },
  ];

  const photos: PhotoTile[] = [
    { id: "arrival", label: t("arrivalPhotoLabel"), url: order.arrival_photo_url },
    order.status === "no_show"
      ? { id: "no-show", label: t("noShowProofLabel"), url: order.no_show_photo_url }
      : { id: "handover", label: t("handoverPhotoLabel"), url: order.handover_photo_url },
  ];

  const hasPhotos = photos.some((p) => p.url);

  return (
    <div className="space-y-4 rounded-xl border border-neutral-200 bg-white p-4 shadow-sm" data-testid="job-summary">
      <h2 className="font-semibold">{t("jobSummaryTitle")}</h2>

      <dl className="divide-y divide-neutral-100 text-sm">
        {timeline.map((row) => (
          <div key={row.id} className="flex items-center justify-between py-2">
            <dt className="text-neutral-500">{row.label}</dt>
            <dd className="font-medium text-neutral-800">{row.value}</dd>
          </div>
        ))}
      </dl>

      <div>
        <span className="mb-2 block text-sm font-medium">{t("photosLabel")}</span>
        {hasPhotos ? (
          <div className="grid grid-cols-2 gap-3">
            {photos.map((photo) =>
              photo.url ? (
                <figure key={photo.id} className="overflow-hidden rounded-md border border-neutral-200">
                  <a href={photo.url} target="_blank" rel="noreferrer">
                    <img src={photo.url} alt={`${photo.label} photo`} className="h-40 w-full object-cover" />
                  </a>
                  <figcaption className="bg-neutral-50 px-2 py-1 text-xs text-neutral-500">{photo.label}</figcaption>
                </figure>
              ) : null
            )}
          </div>
        ) : (
          <p className="text-sm text-neutral-500">{t("noPhotosCaptured")}</p>
        )}
      </div>
    </div>
  );
}
